import { sortBy } from 'lodash';
import type { IntlShape } from 'react-intl';

import { zIndices } from '../../tokens/zIndices';
import { Option } from '../../types/Option';

export const getOptionSelectAll = (intl: IntlShape) => {
  return {
    label: intl.formatMessage({
      id: 'Option.selectAll.label',
      defaultMessage: 'Select All',
      description:
        'Label for the "Select All" option in a multi-select dropdown.',
    }),
    value: '__EDS_SELECT_ALL_VALUE__',
  };
};

// coerce a value to an option
export const coerceOption = <V extends unknown>(
  value: V,
  config: {
    isLoading?: boolean;
    shouldAllowInvalidValue: boolean;
  },
) => ({
  label: config.isLoading
    ? 'Loading value…'
    : config.shouldAllowInvalidValue
      ? String(value)
      : 'Removed value',
  value,
});

export const getSelectAllLabel = (
  intl: IntlShape,
  countString?: string,
  search?: string,
) => {
  const SELECT_ALL_OPTION = getOptionSelectAll(intl);
  const count = Number(countString);
  const exceeding = countString?.endsWith('+');
  return !countString || (isNaN(count) && !exceeding)
    ? SELECT_ALL_OPTION.label
    : search
      ? intl.formatMessage(
          {
            id: 'Select.getSelectAllMatchingLabel',
            defaultMessage:
              '{selectAll} {countString} Matching {count, plural, one {Value} other {Values}}',
            description:
              'Label for Select All the option with matching count. Example: selectAll = "Select All", countString = "5", count = 5',
          },
          {
            selectAll: SELECT_ALL_OPTION.label,
            countString,

            count,
          },
        )
      : intl.formatMessage(
          {
            id: 'Select.getSelectAllLabel',
            defaultMessage:
              '{selectAll} {countString} {count, plural, one {Value} other {Values}}',
            description:
              'Label for Select All option with count. Example: selectAll = "Select All", countString = "5", count = 5',
          },
          {
            selectAll: SELECT_ALL_OPTION.label,
            countString,
            count,
          },
        );
};

// typecast-friendly options.find asserting that an option is always found
export const findOption =
  <V extends unknown>(
    options: Option<V>[],
    shouldAllowInvalidValue: boolean,
    isLoading?: boolean,
  ) =>
  (value: V): Option<V> => {
    const foundOption = (
      testIsOptionsGrouped(options)
        ? options.map((option) => option.options ?? []).flat()
        : options
    ).find((option) => option.value === value);
    return (
      foundOption ??
      coerceOption(value, {
        isLoading,
        shouldAllowInvalidValue,
      })
    );
  };

export const filterGroupedOptions = <V extends unknown>(
  options: Option<V>[],
  filter: (option: Option<V>) => boolean,
) => {
  return testIsOptionsGrouped(options)
    ? options.map((groupedOption) => ({
        ...groupedOption,
        options: groupedOption.options!.filter(filter),
      }))
    : options.filter(filter);
};

export const getStyles = ({
  isEmbedded,
  isWidthRelative,
  maxWidth,
}: {
  isEmbedded?: boolean;
  isWidthRelative?: boolean;
  maxWidth?: number | string;
}) => ({
  container: (base: any) => ({
    ...base,
    flex: isWidthRelative ? 1 : 'none',
  }),
  menu: isEmbedded
    ? () => ({
        position: 'relative',
        maxWidth,
      })
    : undefined,
  menuPortal: (base: any) => ({ ...base, zIndex: zIndices.popover }),
});

export const sortOptions = <V extends unknown>(
  options: Option<V>[],
  value: V | V[] | null,
): Option<V>[] => {
  const valueSet = new Set(Array.isArray(value) ? value : [value]);
  return sortBy(options, [
    (option) => (valueSet.has(option.value) ? -1 : 1), // prioritize sorting selected values
    'label',
  ]);
};

export const testIsOptionDisabled = <V extends unknown>(option: Option<V>) =>
  Boolean(option.disabled);

// all options must be grouped to use grouped logic.  This is an efficient predicate test as the size of groups is generally small.
export const testIsOptionsGrouped = <V extends unknown>(
  options: Option<V>[],
): boolean =>
  options.length > 0 && options.every((option) => Boolean(option.options));
